/**
 * Prestige (v0.4 RFC §3–§5) — pure, Phaser-free. The voluntary "Sell the
 * Facility" loop: cash the run out for Reputation Capital, then reset the run
 * under the (kept) account. Plus the special-upgrade shop's purchase math.
 * Fail-loud per ADR-002: prestiging before the offer letter is read throws;
 * an unaffordable shop buy throws — no silent no-op.
 */

import { LADDER, fireLadderUpgrade } from "@/machines/ladder";
import { fireSell } from "@/machines/runmeta";
import { CARE_TIERS, METERS, PRESTIGE } from "@/data/tunables";
import { frontierTier, newRun, type GameState } from "@/state/GameState";
import { draw } from "@/systems/rng";
import type { GameOverReason } from "@/systems/meters";
import { streetKnowledgePayout } from "@/systems/criminal";
import {
  UPGRADES,
  upgradeCostAt,
  upgradeCurrency,
  upgradeDef,
  upgradeLevel,
  type UpgradeCurrency,
  type UpgradeId,
} from "@/data/upgrades";

/** Completions banked this run (per-tier counter sums both promotions + grads). */
function completionsThisRun(state: GameState): number {
  return CARE_TIERS.reduce((sum, t) => sum + state.run.graduationsByTier[t], 0);
}

/**
 * The Reputation-Capital payout for cashing out NOW (§4). Scales with what the
 * run achieved: completions + peak net/sec + a frontier-depth bonus. Pure read
 * — computing it does not change state (the prestige-screen preview calls it).
 */
export function prestigePayout(state: GameState): number {
  const frontier = frontierTier(state);
  const frontierBonus = frontier === null ? 0 : PRESTIGE.frontierBonus[frontier];
  const raw =
    completionsThisRun(state) * PRESTIGE.perGraduation +
    state.run.peakNetPerSec * PRESTIGE.perPeakNet +
    frontierBonus;
  return Math.max(0, Math.floor(raw));
}

export interface PrestigeResult {
  payout: number;
  /** The fresh run's RNG seed (deterministic — drawn from the outgoing run). */
  seed: number;
  /** Set only for a FORCED prestige — why the run ended. */
  reason?: GameOverReason;
}

/**
 * Sell the facility. Requires the offer letter to have been read this run
 * (fail-loud otherwise). Banks the payout onto the account, then resets the run
 * under that same account — high-water reopens the earned tiers, head-start
 * upgrades apply. The account (Reputation Capital, upgrades, high-water) is
 * KEPT; everything in the run is wiped.
 */
export function prestige(state: GameState): PrestigeResult {
  // v0.10 M4: the RunMeta machine arbitrates SELL (guard = the open offer).
  if (!fireSell(state)) {
    throw new Error("prestige: no offer is open — the 'Sell the Facility' letter must be read first");
  }
  const payout = prestigePayout(state);
  // Deterministic fresh seed: draw once from the OUTGOING run's rng, so a replay
  // (same account + same pre-prestige state) reproduces the next run exactly.
  const seed = Math.floor(draw(state.run.rng) * 0x1_0000_0000) >>> 0;
  state.account.reputationCapital += payout;
  state.account.streetKnowledge += streetKnowledgePayout(state); // v0.6 criminal currency
  state.run = newRun(seed, state.account);
  return { payout, seed };
}

/**
 * FORCED prestige (v0.5) — a meter game-over ends the run involuntarily. Same
 * reset as voluntary prestige, but NO offer-gate (it's not a choice) and a
 * REDUCED payout (`METERS.forcedPayoutMult`) so voluntary selling always pays
 * more. High-water is preserved; NEVER a save wipe.
 */
export function forcedPrestige(state: GameState, reason: GameOverReason): PrestigeResult {
  // A federal raid takes MORE than a quiet state shutdown/collapse (§11.2).
  const mult = reason === "raid" ? METERS.raidPayoutMult : METERS.forcedPayoutMult;
  const payout = Math.max(0, Math.floor(prestigePayout(state) * mult));
  const seed = Math.floor(draw(state.run.rng) * 0x1_0000_0000) >>> 0;
  state.account.reputationCapital += payout;
  state.account.streetKnowledge += streetKnowledgePayout(state); // v0.6 criminal currency
  state.run = newRun(seed, state.account);
  return { payout, seed, reason };
}

// ── the special-upgrade shop ────────────────────────────────────────────────

export interface ShopEntry {
  id: UpgradeId;
  level: number;
  maxed: boolean;
  /** Cost of the next level in this upgrade's currency (undefined when maxed). */
  nextCost: number | undefined;
  /** Which prestige currency prices this upgrade (v0.6: RC or Street Knowledge). */
  currency: UpgradeCurrency;
  affordable: boolean;
}

/** The account's balance in a given prestige currency. */
function currencyBalance(account: GameState["account"], currency: UpgradeCurrency): number {
  return currency === "streetKnowledge" ? account.streetKnowledge : account.reputationCapital;
}

/** One display row per upgrade, in table order (the DOM shop renders these). */
export function shopEntries(state: GameState): ShopEntry[] {
  return UPGRADES.map((def) => shopEntry(state, def.id));
}

function shopEntry(state: GameState, id: UpgradeId): ShopEntry {
  const def = upgradeDef(id);
  const level = upgradeLevel(state.account, id);
  const maxed = level >= def.maxLevel;
  const nextCost = maxed ? undefined : upgradeCostAt(state.account, id);
  const currency = upgradeCurrency(def);
  return {
    id,
    level,
    maxed,
    nextCost,
    currency,
    affordable: nextCost !== undefined && currencyBalance(state.account, currency) >= nextCost,
  };
}

/**
 * Buy the next level of an upgrade. Fail-loud: unknown id, already-maxed, or
 * unaffordable each throw naming the reason — never a silent no-op.
 */
export function purchaseUpgrade(state: GameState, id: UpgradeId): void {
  // The shop is prestige-screen-scoped (RFC §5/§8): only reachable while a
  // "Sell the Facility" offer is open. Enforce that in the system layer so a
  // caller can't spend RC outside the intended lifecycle (fail-loud, not just
  // a UI convention).
  if (!state.run.prestigeOffered) {
    throw new Error("purchaseUpgrade: the shop is only open during a prestige offer");
  }
  const def = upgradeDef(id); // unknown id fails here, field-named
  const currency = upgradeCurrency(def);
  // v0.10 M2: the Ladder machine arbitrates (same messages preserved).
  const res = fireLadderUpgrade(LADDER.shop(id), state);
  if (!res.allowed) {
    if (res.reason === "maxed") {
      throw new Error(`purchaseUpgrade: "${id}" is already at max level ${def.maxLevel}`);
    }
    if (res.reason === "gated") {
      throw new Error(`purchaseUpgrade: ${res.gateMessage}`);
    }
    const label = currency === "streetKnowledge" ? "Street Knowledge" : "Reputation Capital";
    const balance = currencyBalance(state.account, currency);
    throw new Error(`purchaseUpgrade: "${id}" costs ${res.cost} ${label}, have ${Math.floor(balance)}`);
  }
  if (currency === "streetKnowledge") state.account.streetKnowledge -= res.cost;
  else state.account.reputationCapital -= res.cost;
  state.account.upgrades[id] = res.newLevel;
}
